import { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, DollarSign, TrendingUp, Calendar, PieChart, Loader2, Save, History, Trash2, Upload, CalendarDays } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ThemeToggle } from "@/components/ThemeToggle";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartsPie, Pie, Cell, LineChart, Line, Legend } from "recharts";
import { PortfolioFileImport, ImportedPosition } from "@/components/PortfolioFileImport";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { format } from "date-fns";
import DividendCalendar from "@/components/DividendCalendar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const COLORS = ["hsl(var(--primary))", "hsl(var(--accent))", "#f59e0b", "#10b981", "#8b5cf6", "#ec4899", "#06b6d4", "#f97316"];

interface SavedSnapshot {
  id: string;
  created_at: string;
  positions_count: number;
  total_value: number;
  positions_data: any[];
  analysis_data: any;
}

export default function DividendDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [ibkrData, setIbkrData] = useState<any>(null);
  const [importedPositions, setImportedPositions] = useState<ImportedPosition[]>([]);
  const [showImport, setShowImport] = useState(false);
  const [savedSnapshots, setSavedSnapshots] = useState<SavedSnapshot[]>([]);
  const [saving, setSaving] = useState(false);
  const [dataSource, setDataSource] = useState<'ibkr' | 'import'>('ibkr');

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setUser(session?.user ?? null));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => setUser(session?.user ?? null));
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;
    loadDividendData();
    loadSnapshots();
  }, [user]);

  const loadDividendData = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("ibkr_imports")
      .select("dividends_data, positions_data, total_dividends, imported_at")
      .eq("user_id", user.id)
      .order("imported_at", { ascending: false })
      .limit(1)
      .single();
    if (!error && data) { setIbkrData(data); setDataSource('ibkr'); }
    setLoading(false);
  };

  const loadSnapshots = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from("portfolio_snapshots")
      .select("*")
      .eq("user_id", user.id)
      .eq("snapshot_type", "dividend")
      .order("created_at", { ascending: false })
      .limit(10);
    setSavedSnapshots((data as any[]) || []);
  }, [user]);

  const handleImport = (positions: ImportedPosition[]) => {
    setImportedPositions(positions);
    setDataSource('import');
    setShowImport(false);
  };

  const saveSnapshot = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const posData = dataSource === 'import' ? importedPositions : (ibkrData?.positions_data || []);
      const totalVal = dataSource === 'import'
        ? importedPositions.reduce((s, p) => s + p.totalValue, 0)
        : (ibkrData?.total_dividends || 0);
      const { error } = await supabase.from("portfolio_snapshots").insert({
        user_id: user.id,
        snapshot_type: "dividend",
        positions_data: posData as any,
        analysis_data: { dividends: ibkrData?.dividends_data || [], totalDividends: totalDividendIncome, yieldOnCost, projectedAnnual, source: dataSource } as any,
        metadata: { source: dataSource, importedAt: new Date().toISOString() } as any,
        positions_count: dataSource === 'import' ? importedPositions.length : positions.length,
        total_value: totalVal,
      });
      if (error) throw error;
      toast({ title: "Saved", description: "Dividend analysis saved." });
      loadSnapshots();
    } catch (e: any) {
      toast({ title: "Save Failed", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const restoreSnapshot = (snap: SavedSnapshot) => {
    const analysis = snap.analysis_data as any;
    if (analysis?.source === 'import') {
      setImportedPositions(snap.positions_data as ImportedPosition[]);
      setDataSource('import');
    } else {
      setIbkrData({ dividends_data: analysis?.dividends || [], positions_data: snap.positions_data, total_dividends: analysis?.totalDividends || 0 });
      setDataSource('ibkr');
    }
    toast({ title: "Restored", description: `Loaded snapshot from ${format(new Date(snap.created_at), "dd MMM yyyy HH:mm")}` });
  };

  const deleteSnapshot = async (id: string) => {
    await supabase.from("portfolio_snapshots").delete().eq("id", id);
    setSavedSnapshots(prev => prev.filter(s => s.id !== id));
    toast({ title: "Deleted" });
  };

  // Data sources
  const dividends = useMemo(() => {
    if (dataSource === 'ibkr' && ibkrData?.dividends_data) {
      return (ibkrData.dividends_data as any[]).filter((d: any) => d && (d.amount || d.total || d.net));
    }
    return [];
  }, [ibkrData, dataSource]);

  const positions = useMemo(() => {
    if (dataSource === 'import') return importedPositions;
    if (ibkrData?.positions_data) return ibkrData.positions_data as any[];
    return [];
  }, [ibkrData, importedPositions, dataSource]);

  const byStock = useMemo(() => {
    if (dataSource === 'import') {
      return importedPositions
        .map(p => ({ name: p.stockName.substring(0, 15), total: Math.round(p.totalValue * 100) / 100 }))
        .sort((a, b) => b.total - a.total);
    }
    const map = new Map<string, number>();
    dividends.forEach((d: any) => {
      const name = d.symbol || d.description || d.stock || "Unknown";
      const amount = Math.abs(d.amount || d.total || d.net || 0);
      map.set(name, (map.get(name) || 0) + amount);
    });
    return Array.from(map.entries()).map(([name, total]) => ({ name: name.substring(0, 15), total: Math.round(total * 100) / 100 })).sort((a, b) => b.total - a.total);
  }, [dividends, importedPositions, dataSource]);

  const monthlyData = useMemo(() => {
    if (dataSource === 'import') return [];
    const map = new Map<string, number>();
    dividends.forEach((d: any) => {
      const date = d.date || d.payDate || d.reportDate || "";
      const month = date ? date.substring(0, 7) : "Unknown";
      const amount = Math.abs(d.amount || d.total || d.net || 0);
      map.set(month, (map.get(month) || 0) + amount);
    });
    return Array.from(map.entries()).map(([month, total]) => ({ month, total: Math.round(total * 100) / 100 })).sort((a, b) => a.month.localeCompare(b.month));
  }, [dividends, dataSource]);

  const totalDividendIncome = useMemo(() => {
    if (dataSource === 'import') return importedPositions.reduce((s, p) => s + p.totalValue, 0);
    return dividends.reduce((sum: number, d: any) => sum + Math.abs(d.amount || d.total || d.net || 0), 0);
  }, [dividends, importedPositions, dataSource]);

  const yieldOnCost = useMemo(() => {
    const totalInvested = positions.reduce((sum: number, p: any) => {
      if (dataSource === 'import') return sum + (p.initialInvestment || 0);
      const qty = p.quantity || p.shares || 0;
      const cost = p.costBasis || p.avgCost || p.buyPrice || 0;
      return sum + Math.abs(qty * cost);
    }, 0);
    if (totalInvested === 0) return 0;
    return (totalDividendIncome / totalInvested) * 100;
  }, [positions, totalDividendIncome, dataSource]);

  const projectedAnnual = useMemo(() => {
    if (dataSource === 'import') return totalDividendIncome; // estimation
    if (monthlyData.length === 0) return 0;
    return (totalDividendIncome / Math.max(monthlyData.length, 1)) * 12;
  }, [monthlyData, totalDividendIncome, dataSource]);

  const hasData = dividends.length > 0 || importedPositions.length > 0;

  // Yearly summary from monthly data
  const yearlyData = useMemo(() => {
    if (monthlyData.length === 0) return [];
    const map = new Map<string, number>();
    monthlyData.forEach(m => {
      const year = m.month.substring(0, 4);
      map.set(year, (map.get(year) || 0) + m.total);
    });
    return Array.from(map.entries())
      .map(([year, total]) => ({ year, total: Math.round(total * 100) / 100 }))
      .sort((a, b) => a.year.localeCompare(b.year));
  }, [monthlyData]);

  // Year-over-year growth
  const yearlyWithGrowth = useMemo(() => {
    return yearlyData.map((y, i) => ({
      ...y,
      growth: i > 0 ? Math.round(((y.total - yearlyData[i - 1].total) / yearlyData[i - 1].total) * 10000) / 100 : null,
    }));
  }, [yearlyData]);

  // CAGR-based projection
  const projectionData = useMemo((): { data: { year: string; actual: number | null; projected: number | null }[]; cagr: number } | null => {
    if (yearlyData.length < 2) return null;
    const first = yearlyData[0];
    const last = yearlyData[yearlyData.length - 1];
    const years = parseInt(last.year) - parseInt(first.year);
    if (years <= 0 || first.total <= 0) return null;
    const cagr = Math.pow(last.total / first.total, 1 / years) - 1;

    const data: { year: string; actual: number | null; projected: number | null }[] = [];
    yearlyData.forEach(y => data.push({ year: y.year, actual: y.total, projected: null }));

    const lastYear = parseInt(last.year);
    for (let i = 1; i <= 5; i++) {
      const projValue = Math.round(last.total * Math.pow(1 + cagr, i) * 100) / 100;
      data.push({ year: String(lastYear + i), actual: null, projected: projValue });
    }

    return { data, cagr: Math.round(cagr * 10000) / 100 };
  }, [yearlyData]);

  // Extract tickers and quantities for dividend calendar
  const calendarTickers = useMemo(() => {
    if (dataSource === 'import') return importedPositions.map(p => p.ticker).filter(Boolean);
    return (positions as any[]).map((p: any) => p.symbol || p.ticker).filter(Boolean);
  }, [positions, importedPositions, dataSource]);

  const calendarQuantities = useMemo(() => {
    const map: Record<string, number> = {};
    if (dataSource === 'import') {
      importedPositions.forEach(p => { if (p.ticker) map[p.ticker] = p.quantity; });
    } else {
      (positions as any[]).forEach((p: any) => {
        const sym = p.symbol || p.ticker;
        if (sym) map[sym] = Math.abs(p.quantity || p.shares || 1);
      });
    }
    return map;
  }, [positions, importedPositions, dataSource]);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-amber-500/10 rounded-lg">
                <DollarSign className="h-5 w-5 text-amber-500" />
              </div>
              <h1 className="text-xl font-bold text-foreground">Dividend Dashboard</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasData && (
              <Button variant="outline" size="sm" onClick={saveSnapshot} disabled={saving} className="gap-2">
                <Save className="h-4 w-4" />{saving ? 'Saving...' : 'Save'}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setShowImport(!showImport)} className="gap-2">
              <Upload className="h-4 w-4" />Import
            </Button>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6">
        {/* Import Panel */}
        <Collapsible open={showImport} onOpenChange={setShowImport}>
          <CollapsibleContent>
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-base">Import Portfolio Data</CardTitle>
                <CardDescription>Upload an Excel file (PRVFIN tab) or IBKR Activity Statement</CardDescription>
              </CardHeader>
              <CardContent>
                <PortfolioFileImport onImportComplete={handleImport} buttonLabel="Import for Dividend Analysis" />
              </CardContent>
            </Card>
          </CollapsibleContent>
        </Collapsible>

        {/* Saved Sessions */}
        {savedSnapshots.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><History className="h-4 w-4" /> Saved Analyses ({savedSnapshots.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {savedSnapshots.map(snap => (
                  <div key={snap.id} className="flex items-center gap-1">
                    <Button variant="outline" size="sm" onClick={() => restoreSnapshot(snap)} className="text-xs">
                      {format(new Date(snap.created_at), "dd MMM HH:mm")} • {snap.positions_count} pos
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteSnapshot(snap.id)}>
                      <Trash2 className="h-3 w-3 text-muted-foreground" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : !hasData ? (
          <Card className="text-center py-16">
            <CardContent>
              <DollarSign className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">No Dividend Data</h2>
              <p className="text-muted-foreground mb-4">Import an IBKR statement or Excel file, or load a saved analysis.</p>
              <Button onClick={() => setShowImport(true)} className="gap-2"><Upload className="h-4 w-4" />Import Data</Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {dataSource === 'import' && (
              <Badge variant="outline" className="text-xs">Data from imported file • {importedPositions.length} positions</Badge>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground mb-1">Total Dividend Income</p><p className="text-2xl font-bold text-foreground">${totalDividendIncome.toFixed(2)}</p></CardContent></Card>
              <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground mb-1">Yield on Cost</p><p className="text-2xl font-bold text-amber-500">{yieldOnCost.toFixed(2)}%</p></CardContent></Card>
              <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground mb-1">Projected Annual</p><p className="text-2xl font-bold text-emerald-500">${projectedAnnual.toFixed(2)}</p></CardContent></Card>
              <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground mb-1">Dividend Stocks</p><p className="text-2xl font-bold text-foreground">{byStock.length}</p></CardContent></Card>
            </div>

            {monthlyData.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Calendar className="h-4 w-4 text-primary" /> Monthly Dividend Income</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis dataKey="month" tick={{ fill: "hsl(var(--muted-foreground))" }} />
                      <YAxis tick={{ fill: "hsl(var(--muted-foreground))" }} />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} />
                      <Bar dataKey="total" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} name="Dividend $" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><PieChart className="h-4 w-4 text-amber-500" /> Income by Stock</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <RechartsPie>
                      <Pie data={byStock.slice(0, 8)} dataKey="total" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                        {byStock.slice(0, 8).map((_, i) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
                      </Pie>
                      <Tooltip />
                    </RechartsPie>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4 text-emerald-500" /> Top Dividend Payers</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {byStock.slice(0, 10).map((stock, i) => (
                      <div key={stock.name} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="w-6 h-6 flex items-center justify-center text-[10px] p-0">{i + 1}</Badge>
                          <span className="text-sm font-medium truncate max-w-[150px]">{stock.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-primary rounded-full" style={{ width: `${(stock.total / (byStock[0]?.total || 1)) * 100}%` }} />
                          </div>
                          <span className="text-sm font-semibold text-foreground w-16 text-right">${stock.total}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {monthlyData.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" /> Cumulative Dividend Income</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={monthlyData.reduce((acc: any[], item, i) => {
                      const prev = i > 0 ? acc[i - 1].cumulative : 0;
                      acc.push({ ...item, cumulative: Math.round((prev + item.total) * 100) / 100 });
                      return acc;
                    }, [])}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis dataKey="month" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                      <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} />
                      <Line type="monotone" dataKey="cumulative" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ fill: "hsl(var(--primary))", r: 3 }} name="Cumulative $" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Yearly Dividend Income Summary */}
            {yearlyWithGrowth.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 text-amber-500" /> Yearly Dividend Income Summary
                  </CardTitle>
                  <CardDescription>Annual totals with year-over-year growth</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={yearlyWithGrowth}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis dataKey="year" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} />
                      <YAxis yAxisId="left" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                      <Tooltip
                        contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }}
                        formatter={(value: any, name: string) => {
                          if (name === 'Total') return [`$${Number(value).toFixed(2)}`, name];
                          if (name === 'YoY Growth') return [value != null ? `${Number(value).toFixed(1)}%` : 'N/A', name];
                          return [value, name];
                        }}
                      />
                      <Legend />
                      <Bar yAxisId="left" dataKey="total" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} name="Total" />
                      <Line yAxisId="right" type="monotone" dataKey="growth" stroke="hsl(142 76% 36%)" strokeWidth={2} dot={{ r: 4, fill: "hsl(142 76% 36%)" }} name="YoY Growth" connectNulls />
                    </BarChart>
                  </ResponsiveContainer>

                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Year</TableHead>
                          <TableHead className="text-right">Total Income</TableHead>
                          <TableHead className="text-right">YoY Growth</TableHead>
                          <TableHead className="text-right">Avg / Month</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {yearlyWithGrowth.map(y => (
                          <TableRow key={y.year}>
                            <TableCell className="font-medium">{y.year}</TableCell>
                            <TableCell className="text-right font-semibold">${y.total.toFixed(2)}</TableCell>
                            <TableCell className="text-right">
                              {y.growth != null ? (
                                <Badge variant="outline" className={y.growth >= 0 ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20" : "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20"}>
                                  {y.growth >= 0 ? "+" : ""}{y.growth.toFixed(1)}%
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground text-sm">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground">${(y.total / 12).toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Dividend Growth Projection */}
            {projectionData && projectionData.data.length > 0 && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-emerald-500" /> Dividend Growth Projection
                      </CardTitle>
                      <CardDescription>5-year forecast based on historical CAGR of {projectionData.cagr}%</CardDescription>
                    </div>
                    <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20">
                      CAGR: {projectionData.cagr}%
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={projectionData.data}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis dataKey="year" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} />
                      <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                      <Tooltip
                        contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }}
                        formatter={(value: any, name: string) => value != null ? [`$${Number(value).toFixed(2)}`, name === 'actual' ? 'Actual' : 'Projected'] : []}
                      />
                      <Legend formatter={(v) => v === 'actual' ? 'Actual' : 'Projected'} />
                      <Bar dataKey="actual" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} name="actual" />
                      <Bar dataKey="projected" fill="hsl(142 76% 36% / 0.5)" radius={[4, 4, 0, 0]} name="projected" stroke="hsl(142 76% 36%)" strokeWidth={1} strokeDasharray="4 2" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Dividend Calendar */}
            {calendarTickers.length > 0 && (
              <DividendCalendar tickers={calendarTickers} quantities={calendarQuantities} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
